import { IFormSchemaV2 } from "@graffer-inc/form-schema";

import { MigrationError } from "../error";
import { SmartApplyFormSchemaValidator } from "../../domain/procedure/form-schema/smart-apply-form-schema-validator";
import {
  ApplicantSchemaKeys,
  DefaultApplicantSchemaKeys,
} from "../../domain/procedure/procedure-detail";
import { CloudGovFormSchema } from "../../domain/procedure/form-schema/cloudgov-form-schema";
import { hasIgnorableValidationErrorsOnly } from "../util";

type Response = ApplicantSchemaKeys | MigrationError;

export const up = (formSchema: IFormSchemaV2): Response => {
  // 古いスキーマだと、 constructor 呼び出しやバリデーション実行でランタイムエラーが
  // 出ることがあるので、 try catch で囲む。
  try {
    if (!isValidSchemaToStartMigration(new CloudGovFormSchema(formSchema))) {
      return new MigrationError(
        "VALIDATION_ERROR_BEFORE_MIGRATION",
        new SmartApplyFormSchemaValidator(
          formSchema,
          DefaultApplicantSchemaKeys,
        ).validateDeep(),
      );
    }
  } catch (error) {
    if (error instanceof Error) {
      return new MigrationError("VALIDATION_ERROR_BEFORE_MIGRATION", [
        `name: ${error.name}, message: ${error.message}`,
      ]);
    }

    return new MigrationError("VALIDATION_ERROR_BEFORE_MIGRATION", [
      JSON.stringify(error),
    ]);
  }

  return extractApplicantSchemaKeys(formSchema);
};

const isValidSchemaToStartMigration = (formSchema: IFormSchemaV2): boolean => {
  const validator = new SmartApplyFormSchemaValidator(
    formSchema,
    DefaultApplicantSchemaKeys,
  );
  if (validator.isValid()) {
    return true;
  }

  if (hasIgnorableValidationErrorsOnly(validator)) {
    return true;
  }

  const validationErrors = validator.validateDeep();

  // 申請者セクションからデータを抽出する上では不都合がないので、
  // totalInputCount() によるエラーは見逃す
  if (
    validationErrors.length === 1 &&
    validationErrors[0] === validator.totalInputCount()
  ) {
    return true;
  }

  return false;
};

const extractApplicantSchemaKeys = (
  formSchema: IFormSchemaV2,
): ApplicantSchemaKeys => {
  const result: ApplicantSchemaKeys = {
    applicantSection: {
      id: "",
      formSchemaJsonPath: "",
    },
    applicantType: {
      id: "",
      formSchemaJsonPath: "",
    },
    applicantCorporateSearch: {
      id: "",
      formSchemaJsonPath: "",
    },
    applicantName: {
      id: "",
      formSchemaJsonPath: "",
    },
    applicantNameKana: {
      id: "",
      formSchemaJsonPath: "",
    },
    applicantPostalCode: {
      id: "",
      formSchemaJsonPath: "",
    },
    applicantAddress: {
      id: "",
      formSchemaJsonPath: "",
    },
    applicantBirthDate: {
      id: "",
      formSchemaJsonPath: "",
    },
    applicantTradeName: {
      id: "",
      formSchemaJsonPath: "",
    },
    applicantTradeNameKana: {
      id: "",
      formSchemaJsonPath: "",
    },
    applicantCorporateNumber: {
      id: "",
      formSchemaJsonPath: "",
    },
    applicantRepresentativeName: {
      id: "",
      formSchemaJsonPath: "",
    },
    applicantPhoneNumber: {
      id: "",
      formSchemaJsonPath: "",
    },
    applicantEmail: {
      id: "",
      formSchemaJsonPath: "",
    },
    applicantContactPersonal: {
      id: "",
      formSchemaJsonPath: "",
    },
  };

  const cloudGovFormSchema = new CloudGovFormSchema(formSchema);
  const applicantSection = cloudGovFormSchema.applicantSection(
    DefaultApplicantSchemaKeys,
  );

  result.applicantSection = {
    id: applicantSection.id,
    // validation を経由できている時点で applicantSection の path は次の内容としてよい
    // https://github.com/graffer-inc/cloud-gov/blob/333b6ffc8481dd86ae75c8f4350f1ddf2823e38f/fe-core/lib/domain/procedure/form-schema/smart-apply-form-schema-validator/smart-apply-form-schema-validator.ts#L172-L181
    formSchemaJsonPath: "root.0",
  };

  const reservedApplicantIds = Object.keys(
    DefaultApplicantSchemaKeys,
  ) as (keyof typeof DefaultApplicantSchemaKeys)[];
  reservedApplicantIds
    .filter((key) => key !== DefaultApplicantSchemaKeys.applicantSection.id)
    .forEach((key) => {
      const elementIndex = applicantSection.children.findIndex(
        ({ id }) => id === key,
      );
      if (elementIndex === -1) {
        return;
      }
      result[key] = {
        id: key,
        formSchemaJsonPath: `${result.applicantSection.formSchemaJsonPath}.children.${elementIndex}`,
      };
    });

  return result;
};
